(function () {
  'use strict';

  if (window.__siblingBundleGuardLoaded) return;
  window.__siblingBundleGuardLoaded = true;

  var VERSION = '2026-05-24-v15';
  console.log('[BundleGuard] ' + VERSION + ' loaded on', window.location.href);

  // ── KILL SWITCH ───────────────────────────────────────────────────────────────

  window.DISABLE_BUNDLE_GUARD = window.DISABLE_BUNDLE_GUARD || false;

  // ── EXPLICIT BUNDLE PROPERTY NAMES ───────────────────────────────────────────
  //
  // These properties are stamped by gymgrl-product-hero.liquid at add-to-cart time.
  //
  // _bundle_id              — shared ID linking all siblings in one add-to-cart call
  // _bundle_type            — must be 'subscription_bundle' to be enforced
  // _bundle_required_count  — how many siblings the bundle requires (>1 = enforced)
  // _bundle_selling_plan_id — the selling plan ID used for this subscription package
  //
  // Buy 1 standalone: bundleSize=1 → properties are NOT stamped → never enforced.
  // Buy 2 bundle:     bundleSize=2 → _bundle_required_count='2' → enforced.
  // Buy 3 bundle:     bundleSize=3 → _bundle_required_count='3' → enforced.

  var BUNDLE_ID_PROPERTY            = '_bundle_id';
  var BUNDLE_TYPE_PROPERTY          = '_bundle_type';
  var BUNDLE_REQUIRED_COUNT_PROPERTY = '_bundle_required_count';
  var BUNDLE_SELLING_PLAN_ID_PROPERTY = '_bundle_selling_plan_id';

  // ── LOCK ──────────────────────────────────────────────────────────────────────

  var isBundleGuardUpdating = false;

  // ── PREVIOUS QUANTITIES ───────────────────────────────────────────────────────
  // item.key → quantity snapshot, used to detect which sibling the customer changed.

  var previousQuantitiesByKey = new Map();

  // Debounce handle.
  var validateTimer = null;

  // ── PROPERTY HELPERS ──────────────────────────────────────────────────────────

  function getRequiredCount(item) {
    var raw = item && item.properties && item.properties[BUNDLE_REQUIRED_COUNT_PROPERTY];
    var count = Number(raw);
    return Number.isFinite(count) ? count : 1;
  }

  // Returns true ONLY for subscription bundle lines that require sibling enforcement.
  // A line must have ALL of:
  //   • an active selling plan (subscription)
  //   • _bundle_id (links siblings)
  //   • _bundle_type === 'subscription_bundle' (explicit bundle marker)
  //   • _bundle_required_count > 1 (multi-product bundle; Buy 1 is excluded)
  //
  // Normal products, one-time purchases, and Buy 1 standalones all return false.
  function isEnforcedSubscriptionBundleLine(item) {
    if (!item || !item.key) return false;
    if (!item.selling_plan_allocation) return false;
    if (!item.properties) return false;
    if (!item.properties[BUNDLE_ID_PROPERTY]) return false;
    if (item.properties[BUNDLE_TYPE_PROPERTY] !== 'subscription_bundle') return false;
    return getRequiredCount(item) > 1;
  }

  // ── CART HELPERS ──────────────────────────────────────────────────────────────

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCart() {
    return fetch(cartRoot() + 'cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    }).then(function (r) {
      if (!r.ok) throw new Error('[BundleGuard] cart.js ' + r.status);
      return r.json();
    });
  }

  function getEnforcedBundleGroups(cart) {
    var groups = new Map();
    cart.items.forEach(function (item) {
      if (!isEnforcedSubscriptionBundleLine(item)) return;
      var bid = item.properties[BUNDLE_ID_PROPERTY];
      if (!groups.has(bid)) groups.set(bid, []);
      groups.get(bid).push(item);
    });
    return groups;
  }

  function updatePreviousQuantities(cart) {
    cart.items.forEach(function (item) {
      previousQuantitiesByKey.set(item.key, item.quantity);
    });
  }

  // ── SAFE CART UPDATE ──────────────────────────────────────────────────────────
  // Fetches a fresh cart and verifies every key in `updates` belongs to an enforced
  // bundle line before sending the request. Normal products can never be touched.

  async function updateCartByKeys(updates, context) {
    var cart = await getCart();
    var byKey = new Map(cart.items.map(function (i) { return [i.key, i]; }));

    var unsafeKeys = Object.keys(updates).filter(function (k) {
      return !isEnforcedSubscriptionBundleLine(byKey.get(k));
    });

    if (unsafeKeys.length) {
      console.error('[BundleGuard] ABORTED — unsafe update attempt', {
        context: context,
        unsafeKeys: unsafeKeys,
        updates: updates
      });
      return null;
    }

    console.warn('[BundleGuard] Applying enforced bundle update', {
      context: context,
      updates: updates
    });

    var sids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset && el.dataset.sectionId) sids.push(el.dataset.sectionId);
    });

    var payload = { updates: updates };
    if (sids.length) payload.sections = sids.join(',');

    var r = await fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!r.ok) throw new Error('[BundleGuard] cart/update.js ' + r.status);
    return r.json();
  }

  // ── UI REFRESH ────────────────────────────────────────────────────────────────

  function refreshCartUI(updatedCart) {
    // Dispatch cart:update with the sections HTML so cart-items-component
    // can call morphSection without an extra server round-trip.
    // No source is set, so this guard's own listener ignores the event.
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: updatedCart,
        data: {
          sections: updatedCart.sections || {},
          itemCount: updatedCart.item_count
        }
      }
    }));
  }

  // ── MAIN VALIDATION ───────────────────────────────────────────────────────────

  async function validateEnforcedBundles(context) {
    context = context || {};

    if (window.DISABLE_BUNDLE_GUARD) {
      console.warn('[BundleGuard] disabled');
      return;
    }

    if (isBundleGuardUpdating) return;
    isBundleGuardUpdating = true;

    try {
      var cart = await getCart();
      var groups = getEnforcedBundleGroups(cart);
      var updates = {};

      groups.forEach(function (items, bundleId) {
        var requiredCount = getRequiredCount(items[0]);

        // Safety: should never be <= 1 because isEnforcedSubscriptionBundleLine
        // already requires > 1, but guard defensively anyway.
        if (requiredCount <= 1) return;

        // ── Orphan / incomplete group: fewer siblings than required ────────────
        // Remove all remaining siblings. They cannot exist without their partners.
        if (items.length < requiredCount) {
          console.log('[BundleGuard] Incomplete group — removing remaining siblings', {
            bundleId: bundleId,
            present: items.length,
            required: requiredCount,
            items: items.map(function (i) { return { title: i.product_title, key: i.key }; })
          });
          items.forEach(function (item) { updates[item.key] = 0; });
          return;
        }

        // ── Full group: check for quantity mismatch ────────────────────────────
        if (items.length === requiredCount) {
          var firstQty = items[0].quantity;
          var allEqual = items.every(function (i) { return i.quantity === firstQty; });
          if (allEqual) return;

          // Determine the target quantity:
          // 1. The item the customer explicitly changed (most accurate).
          var targetQty = null;

          if (context.changedKey) {
            items.forEach(function (i) {
              if (i.key === context.changedKey) targetQty = i.quantity;
            });
          }

          // 2. The requested quantity passed from the cart change event.
          if (targetQty === null && typeof context.requestedQuantity === 'number') {
            targetQty = context.requestedQuantity;
          }

          // 3. Whichever sibling has a quantity that differs from the previous snapshot.
          if (targetQty === null) {
            items.forEach(function (item) {
              if (targetQty !== null) return;
              var prev = previousQuantitiesByKey.get(item.key);
              if (typeof prev === 'number' && prev !== item.quantity) {
                targetQty = item.quantity;
              }
            });
          }

          // 4. Fallback: minimum quantity to avoid unexpectedly increasing order size.
          if (targetQty === null) {
            targetQty = Math.min.apply(null, items.map(function (i) { return i.quantity; }));
          }

          items.forEach(function (item) {
            if (item.quantity !== targetQty) updates[item.key] = targetQty;
          });
        }
      });

      if (!Object.keys(updates).length) {
        console.log('[BundleGuard] No updates needed | source:', context.source);
        // Still update previous quantities so the next event has an accurate baseline.
        updatePreviousQuantities(cart);
        return;
      }

      var updatedCart = await updateCartByKeys(updates, context);
      if (updatedCart) {
        refreshCartUI(updatedCart);
        // Update previous quantities from the post-update cart.
        var latestCart = await getCart();
        updatePreviousQuantities(latestCart);
      }

    } catch (err) {
      console.error('[BundleGuard] validation failed', err);
    } finally {
      isBundleGuardUpdating = false;
    }
  }

  // ── CART:UPDATE LISTENER ──────────────────────────────────────────────────────
  //
  // component-cart-items.js dispatches cart:update with source='cart-items-component'
  // after every remove or quantity change (including +/- button presses and the
  // remove button). This fires on every page that has a cart-items-component:
  // the cart drawer, the /cart page, and any other page the theme renders it on.
  //
  // Add-to-cart events from the product hero or product-form have a different
  // source (or no source), so they never trigger the enforcement logic.
  // This prevents destructive logic from running after a normal add-to-cart.

  document.addEventListener('cart:update', function (e) {
    // Never re-enter during our own update.
    if (isBundleGuardUpdating) return;

    var source = e.detail && e.detail.data && e.detail.data.source;

    // Only act on changes made via the cart UI (drawer or cart page).
    if (source !== 'cart-items-component') return;

    var resource = e.detail && e.detail.resource;
    var currentItems = (resource && Array.isArray(resource.items)) ? resource.items : null;

    // Detect which enforced sibling the customer just changed by comparing
    // the post-change cart items against the previous quantities snapshot.
    var changedKey = null;
    var requestedQuantity = null;

    if (currentItems) {
      currentItems.forEach(function (item) {
        if (changedKey) return; // already found one
        if (!isEnforcedSubscriptionBundleLine(item)) return;
        var prev = previousQuantitiesByKey.get(item.key);
        if (typeof prev === 'number' && item.quantity !== prev) {
          changedKey = item.key;
          requestedQuantity = item.quantity;
        }
      });

      // Update snapshot for the next event comparison.
      currentItems.forEach(function (item) {
        previousQuantitiesByKey.set(item.key, item.quantity);
      });
    }

    // Debounce: absorbs rapid successive events without running validation twice.
    clearTimeout(validateTimer);
    validateTimer = setTimeout(function () {
      validateEnforcedBundles({
        source: source,
        changedKey: changedKey,
        requestedQuantity: requestedQuantity
      });
    }, 400);
  });

  // ── PAGE LOAD INIT ────────────────────────────────────────────────────────────
  // Seed previousQuantitiesByKey so the first +/- press has an accurate baseline.
  // No validation is run on page load — only on actual cart mutations.

  getCart().then(function (cart) {
    updatePreviousQuantities(cart);
    console.log('[BundleGuard] Page load init — ' + cart.items.length + ' cart items indexed');
  }).catch(function (err) {
    console.error('[BundleGuard] Page load init failed', err);
  });

  // ── DIAGNOSTICS ───────────────────────────────────────────────────────────────

  window.bundleGuardDiagnostic = async function () {
    var cart = await getCart();
    var rows = cart.items.map(function (item) {
      return {
        title:                item.title,
        key:                  item.key,
        product_id:           item.product_id,
        variant_id:           item.variant_id,
        quantity:             item.quantity,
        selling_plan:         Boolean(item.selling_plan_allocation),
        selling_plan_id:      (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id) || '',
        bundle_id:            (item.properties && item.properties[BUNDLE_ID_PROPERTY]) || '',
        bundle_type:          (item.properties && item.properties[BUNDLE_TYPE_PROPERTY]) || '',
        required_count:       (item.properties && item.properties[BUNDLE_REQUIRED_COUNT_PROPERTY]) || '',
        bundle_selling_plan_id: (item.properties && item.properties[BUNDLE_SELLING_PLAN_ID_PROPERTY]) || '',
        guard_enforced:       isEnforcedSubscriptionBundleLine(item)
      };
    });
    console.table(rows);
    return rows;
  };

  // Legacy alias.
  window.siblingBundleGuardDiagnostic = window.bundleGuardDiagnostic;

  window.bundleGuardRunNow = function () {
    console.log('[BundleGuard] Manual run');
    isBundleGuardUpdating = false;
    return validateEnforcedBundles({ source: 'manual' });
  };

})();
